/*
 * Northside - behaviour for northside-* sections and blocks.
 * Custom elements only: they initialise themselves on page load and when the
 * theme editor re-renders a section. Nothing here adds products to the cart:
 * cart actions always go through Horizon's native product form.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  /* ------------------------------------------------------------------ */
  /* Header + mega menu                                                  */
  /* ------------------------------------------------------------------ */
  class NsHeader extends HTMLElement {
    connectedCallback() {
      this.mega = this.querySelector('[data-ns-mega]');
      this.burger = this.querySelector('[data-ns-burger]');
      this.toggles = Array.prototype.slice.call(this.querySelectorAll('[data-ns-mega-toggle]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-ns-panel]'));
      this.mq = window.matchMedia('(max-width: 989px)');
      this.closeTimer = null;
      this.openTimer = null;

      if (this.burger) this.burger.addEventListener('click', this.onBurger);
      this.toggles.forEach((btn) => {
        btn.addEventListener('click', this.onToggle);
        btn.parentElement.addEventListener('pointerenter', this.onHoverIn);
      });
      this.addEventListener('pointerleave', this.onHoverOut);
      if (this.mega) this.mega.addEventListener('pointerenter', this.cancelClose);

      var search = this.querySelector('[data-ns-search]');
      if (search) search.addEventListener('click', this.onSearch);

      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKey);
      if (this.mq.addEventListener) this.mq.addEventListener('change', this.close);

      this.syncHeight();
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.syncHeight());
        this.ro.observe(this);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKey);
      if (this.mq && this.mq.removeEventListener) this.mq.removeEventListener('change', this.close);
      if (this.ro) this.ro.disconnect();
    }

    syncHeight() {
      var h = this.querySelector('.ns-header__in');
      var height = h ? h.offsetHeight : this.offsetHeight;
      document.documentElement.style.setProperty('--ns-header-h', height + 'px');
      if (this.hasAttribute('data-sticky')) {
        document.body.style.setProperty('--header-height', height + 'px');
        document.body.style.setProperty('--header-group-height', height + 'px');
      }
    }

    onBurger = (event) => {
      event.preventDefault();
      if (this.dataset.open === 'mobile') this.close();
      else this.open('mobile');
    };

    onToggle = (event) => {
      event.preventDefault();
      var idx = event.currentTarget.getAttribute('data-ns-mega-toggle');
      if (this.dataset.open === 'desktop' && this.dataset.panel === idx) this.close();
      else this.open('desktop', idx);
    };

    onHoverIn = (event) => {
      if (event.pointerType !== 'mouse' || this.mq.matches) return;
      var btn = event.currentTarget.querySelector('[data-ns-mega-toggle]');
      if (!btn) return;
      this.cancelClose();
      clearTimeout(this.openTimer);
      this.openTimer = setTimeout(() => this.open('desktop', btn.getAttribute('data-ns-mega-toggle')), 90);
    };

    onHoverOut = (event) => {
      if (event.pointerType !== 'mouse' || this.dataset.open !== 'desktop') return;
      clearTimeout(this.openTimer);
      this.closeTimer = setTimeout(() => this.close(), 220);
    };

    cancelClose = () => {
      clearTimeout(this.closeTimer);
    };

    onSearch = (event) => {
      var modal = document.getElementById('search-modal');
      if (modal && typeof modal.showDialog === 'function') {
        event.preventDefault();
        this.close();
        modal.showDialog();
      }
    };

    onDocClick = (event) => {
      if (!this.dataset.open) return;
      if (this.contains(event.target)) return;
      this.close();
    };

    onKey = (event) => {
      if (event.key !== 'Escape' || !this.dataset.open) return;
      var wasDesktop = this.dataset.open === 'desktop' ? this.querySelector('[data-ns-mega-toggle][aria-expanded="true"]') : this.burger;
      this.close();
      if (wasDesktop) wasDesktop.focus();
    };

    open(mode, idx) {
      this.dataset.open = mode;
      if (idx) this.dataset.panel = idx;
      else delete this.dataset.panel;
      this.panels.forEach((p) => p.classList.toggle('is-active', mode === 'mobile' || p.getAttribute('data-ns-panel') === idx));
      this.toggles.forEach((t) => t.setAttribute('aria-expanded', mode === 'desktop' && t.getAttribute('data-ns-mega-toggle') === idx ? 'true' : 'false'));
      if (this.burger) this.burger.setAttribute('aria-expanded', mode === 'mobile' ? 'true' : 'false');
    }

    close = () => {
      clearTimeout(this.openTimer);
      delete this.dataset.open;
      delete this.dataset.panel;
      this.panels.forEach((p) => p.classList.remove('is-active'));
      this.toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      if (this.burger) this.burger.setAttribute('aria-expanded', 'false');
    };
  }
  define('ns-header', NsHeader);

  /* ------------------------------------------------------------------ */
  /* Tabs (explorer)                                                     */
  /* ------------------------------------------------------------------ */
  class NsTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-ns-tab]'));
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (e) => this.onKey(e, tab));
      });
    }

    select(tab, focus) {
      var key = tab.getAttribute('data-ns-tab');
      this.tabs.forEach((t) => {
        var on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      this.querySelectorAll('[data-ns-panel]').forEach((p) => {
        p.hidden = p.getAttribute('data-ns-panel') !== key;
      });
      if (focus) tab.focus();
    }

    onKey(event, tab) {
      var i = this.tabs.indexOf(tab);
      if (event.key === 'ArrowRight') this.select(this.tabs[(i + 1) % this.tabs.length], true);
      if (event.key === 'ArrowLeft') this.select(this.tabs[(i - 1 + this.tabs.length) % this.tabs.length], true);
    }
  }
  define('ns-tabs', NsTabs);

  /* ------------------------------------------------------------------ */
  /* All teams modal (explorer): filters by group + accent-free search   */
  /* ------------------------------------------------------------------ */
  var normaliza = function (s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  };

  class NsTeamsModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.grid = this.querySelector('[data-ns-teams-grid]');
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-ns-team]'));
      this.filters = Array.prototype.slice.call(this.querySelectorAll('[data-ns-teams-filter]'));
      this.input = this.querySelector('[data-ns-teams-search]');
      this.empty = this.querySelector('[data-ns-teams-empty]');
      this.status = this.querySelector('[data-ns-teams-status]');
      this.group = 'all';

      if (this.dataset.sort === 'count') {
        this.cards
          .slice()
          .sort((a, b) => (parseInt(b.dataset.count, 10) || 0) - (parseInt(a.dataset.count, 10) || 0))
          .forEach((c) => this.grid.appendChild(c));
      }

      var openBtn = this.querySelector('[data-ns-teams-open]');
      if (openBtn) openBtn.addEventListener('click', () => this.open());
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog || e.target.closest('[data-ns-close]')) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('dmc-lock'));
      this.filters.forEach((f) =>
        f.addEventListener('click', () => {
          this.group = f.getAttribute('data-ns-teams-filter');
          this.filters.forEach((x) => x.setAttribute('aria-pressed', x === f ? 'true' : 'false'));
          this.apply();
        })
      );
      if (this.input) this.input.addEventListener('input', () => this.apply());
    }

    open() {
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      document.documentElement.classList.add('dmc-lock');
      this.apply();
    }

    apply() {
      var q = normaliza(this.input ? this.input.value : '');
      var shown = 0;
      this.cards.forEach((c) => {
        var ok = (this.group === 'all' || c.dataset.group === this.group) && (!q || normaliza(c.dataset.name).indexOf(q) > -1);
        c.hidden = !ok;
        if (ok) shown++;
      });
      if (this.empty) this.empty.hidden = shown > 0;
      if (this.status) this.status.textContent = (this.status.dataset.template || '[n]').replace('[n]', shown);
    }
  }
  define('ns-teams-modal', NsTeamsModal);

  /* ------------------------------------------------------------------ */
  /* Reviews carousel (Judge.me) on mobile: the CSS turns it into a native */
  /* horizontal scroll with snap. Judge.me cancels touchmove for its own   */
  /* slider, which would block that scroll, so its touch handlers are kept */
  /* from seeing touches inside the cards (listeners are passive: they     */
  /* never block scrolling themselves).                                   */
  /* ------------------------------------------------------------------ */
  var reviewsMq = window.matchMedia('(max-width: 749px)');
  var shieldReviews = function (event) {
    if (reviewsMq.matches && event.target.closest && event.target.closest('.jdgm-cards-wrapper')) event.stopPropagation();
  };
  var initReviewsSwipe = function () {
    document.querySelectorAll('.ns-reviews__app:not([data-ns-swipe])').forEach(function (app) {
      app.setAttribute('data-ns-swipe', '');
      ['touchstart', 'touchmove', 'touchend'].forEach(function (type) {
        app.addEventListener(type, shieldReviews, { capture: true, passive: true });
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initReviewsSwipe);
  else initReviewsSwipe();
  document.addEventListener('shopify:section:load', initReviewsSwipe);

  /* ------------------------------------------------------------------ */
  /* PDP 3x2 badge: product photos are fit to the screen height, so the  */
  /* photo is narrower than its slide. The badge follows the real top-   */
  /* right corner of the visible photo (CSS position is the fallback).   */
  /* ------------------------------------------------------------------ */
  var photoBox = function (img) {
    var r = img.getBoundingClientRect();
    if (getComputedStyle(img).objectFit !== 'contain' || !img.naturalWidth || !r.height) return r;
    var ratio = img.naturalWidth / img.naturalHeight;
    var w = r.width;
    var h = r.height;
    if (ratio > w / h) h = w / ratio;
    else w = h * ratio;
    return { top: r.top + (r.height - h) / 2, right: r.left + (r.width + w) / 2, width: w };
  };
  var placePdpBadge = function (gallery) {
    var badge = gallery.querySelector(':scope > .ns-pdp-badge');
    if (!badge) return;
    var frame = (gallery.querySelector('slideshow-container') || gallery).getBoundingClientRect();
    var best = null;
    var bestGap = Infinity;
    gallery.querySelectorAll('slideshow-slide, .product-media-container').forEach(function (slide) {
      var b = slide.getBoundingClientRect();
      var gap = Math.abs(b.left - frame.left);
      if (b.width && gap < bestGap) {
        bestGap = gap;
        best = slide;
      }
    });
    var img = best && best.querySelector('img');
    if (!img) return;
    var box = photoBox(img);
    if (!box.width) return;
    var g = gallery.getBoundingClientRect();
    var inset = window.innerWidth < 750 ? 10 : 14;
    badge.style.top = Math.max(0, Math.round(box.top - g.top + inset)) + 'px';
    badge.style.right = Math.max(0, Math.round(g.right - box.right + inset)) + 'px';
  };
  var initPdpBadge = function () {
    document.querySelectorAll('media-gallery:not([data-ns-badge])').forEach(function (gallery) {
      if (!gallery.querySelector(':scope > .ns-pdp-badge')) return;
      gallery.setAttribute('data-ns-badge', '');
      var update = function () {
        placePdpBadge(gallery);
      };
      update();
      gallery.addEventListener('slideshow:select', function () {
        setTimeout(update, 60);
        setTimeout(update, 450);
      });
      gallery.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', update, { once: true });
      });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(gallery);
      window.addEventListener('load', update);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initPdpBadge);
  else initPdpBadge();
  document.addEventListener('shopify:section:load', initPdpBadge);

  /* ------------------------------------------------------------------ */
  /* Reviews masonry (Loox style): show the first N real reviews (the    */
  /* carousel clones are ignored) and a "Ver mas resenas" button.        */
  /* Judge.me renders the cards after load, so this waits for them.      */
  /* ------------------------------------------------------------------ */
  var initReviewsGrid = function () {
    document.querySelectorAll('.ns-reviews--masonry:not([data-ns-grid])').forEach(function (sec) {
      var app = sec.querySelector('.ns-reviews__app');
      if (!app) return;
      sec.setAttribute('data-ns-grid', '');
      var button = null;
      var timer = null;
      var limit = function () {
        return window.innerWidth < 750 ? parseInt(sec.dataset.visibleMobile, 10) || 6 : parseInt(sec.dataset.visibleDesktop, 10) || 8;
      };
      var apply = function () {
        if (sec.classList.contains('is-expanded')) return;
        var items = app.querySelectorAll('.jdgm-carousel-item:not(.jdgm-clone-before):not(.jdgm-clone-after)');
        if (!items.length) return;
        var n = limit();
        items.forEach(function (it, i) {
          it.classList.toggle('ns-rev-hidden', i >= n);
        });
        if (items.length > n && !button) {
          button = document.createElement('button');
          button.type = 'button';
          button.className = 'ns-reviews__more';
          button.textContent = sec.dataset.moreLabel || 'Ver mas';
          button.addEventListener('click', function () {
            sec.classList.add('is-expanded');
            app.querySelectorAll('.ns-rev-hidden').forEach(function (it) {
              it.classList.remove('ns-rev-hidden');
            });
            button.remove();
          });
          app.insertAdjacentElement('afterend', button);
        }
      };
      apply();
      new MutationObserver(function () {
        clearTimeout(timer);
        timer = setTimeout(apply, 120);
      }).observe(app, { childList: true, subtree: true });
      window.addEventListener('resize', function () {
        clearTimeout(timer);
        timer = setTimeout(apply, 200);
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initReviewsGrid);
  else initReviewsGrid();
  document.addEventListener('shopify:section:load', initReviewsGrid);

  /* ------------------------------------------------------------------ */
  /* Size guide modal + recommender                                      */
  /* ------------------------------------------------------------------ */
  class NsSizeGuide extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      try {
        this.t = JSON.parse(this.querySelector('[data-ns-i18n]').textContent);
      } catch (e) {
        this.t = {};
      }
      var openBtn = this.querySelector('[data-ns-open]');
      if (openBtn) openBtn.addEventListener('click', () => this.open());
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog || e.target.closest('[data-ns-close]')) this.close();
      });
      var form = this.querySelector('[data-ns-finder]');
      if (form) form.addEventListener('submit', (e) => this.onFind(e));
      var pick = this.querySelector('[data-ns-pick]');
      if (pick) pick.addEventListener('click', () => this.pickSize(pick.dataset.size));
    }

    open() {
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      document.documentElement.classList.add('dmc-lock');
    }

    close() {
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.removeAttribute('open');
      document.documentElement.classList.remove('dmc-lock');
    }

    rows() {
      var out = [];
      var heads = Array.prototype.map.call(this.querySelectorAll('[data-ns-table] thead th'), (th) => th.textContent.toLowerCase());
      var hIdx = heads.findIndex((t) => /altura|height|estatura/.test(t));
      var wIdx = heads.findIndex((t) => /peso|weight/.test(t));
      var range = (cell) => {
        var m = cell ? cell.textContent.trim().match(/(\d+)\s*(?:-|a)\s*(\d+)/) : null;
        return m ? [+m[1], +m[2]] : null;
      };
      this.querySelectorAll('[data-ns-table] tbody tr').forEach((tr) => {
        var cells = tr.querySelectorAll('th, td');
        if (!cells.length) return;
        var h = range(cells[hIdx > -1 ? hIdx : cells.length - 1]);
        var w = wIdx > -1 ? range(cells[wIdx]) : null;
        out.push({ size: cells[0].textContent.trim(), min: h ? h[0] : null, max: h ? h[1] : null, wmin: w ? w[0] : null, wmax: w ? w[1] : null });
      });
      return out;
    }

    onFind(event) {
      event.preventDefault();
      var form = event.currentTarget;
      var h = parseFloat(form.elements.h.value);
      var w = parseFloat(form.elements.w.value);
      var box = this.querySelector('[data-ns-result]');
      var text = this.querySelector('[data-ns-result-text]');
      var pick = this.querySelector('[data-ns-pick]');
      box.hidden = false;
      if (!h || !w) {
        text.textContent = this.t.missing || '';
        pick.hidden = true;
        return;
      }
      var rows = this.rows().filter((r) => r.min !== null);
      if (!rows.length) return;
      var idx = rows.findIndex((r) => h <= r.max);
      if (idx === -1) idx = rows.length - 1;
      if (rows[idx].wmax !== null) {
        // Weight column available: move up while the weight is above the recommended range
        while (idx < rows.length - 1 && rows[idx].wmax !== null && w > rows[idx].wmax) idx++;
      } else if (w > h - 95) {
        idx = Math.min(rows.length - 1, idx + 1);
      }
      var size = rows[idx].size;
      var loose = rows[Math.min(rows.length - 1, idx + 1)].size;
      var msg = (this.t.result || '').replace('[size]', size);
      if (loose !== size && this.t.resultLoose) msg += ' ' + this.t.resultLoose.replace('[size]', loose);
      text.textContent = msg;
      if (this.findInput(size)) {
        pick.hidden = false;
        pick.dataset.size = size;
        pick.textContent = (this.t.pick || '').replace('[size]', size);
      } else {
        pick.hidden = true;
      }
    }

    findInput(size) {
      var optionName = (this.dataset.option || '').toLowerCase();
      var scope = this.closest('.shopify-section') || document;
      var inputs = scope.querySelectorAll('input[type="radio"][data-option-name]');
      for (var i = 0; i < inputs.length; i++) {
        var input = inputs[i];
        var name = (input.getAttribute('data-option-name') || '').toLowerCase();
        if (optionName && name !== optionName && name !== 'size' && name !== 'talla') continue;
        if (input.value.toLowerCase() === String(size).toLowerCase() && input.getAttribute('data-option-available') !== 'false') {
          return input;
        }
      }
      return null;
    }

    pickSize(size) {
      var input = this.findInput(size);
      if (!input) return;
      this.close();
      if (!input.checked) input.click();
      var label = input.closest('label') || input;
      label.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    }
  }
  define('ns-size-guide', NsSizeGuide);

  /* ------------------------------------------------------------------ */
  /* Estimated delivery dates (business days)                            */
  /* ------------------------------------------------------------------ */
  class NsEta extends HTMLElement {
    connectedCallback() {
      var min = parseInt(this.dataset.min, 10) || 10;
      var max = parseInt(this.dataset.max, 10) || 14;
      if (max < min) max = min;
      var cutoff = parseInt(this.dataset.cutoff, 10);
      var holidays = (this.dataset.holidays || '').split(/[\s,]+/).filter(Boolean);
      var start = new Date();
      if (!isNaN(cutoff) && start.getHours() >= cutoff) start.setDate(start.getDate() + 1);

      function iso(d) {
        return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      }
      function addBusiness(from, n) {
        var d = new Date(from);
        while (n > 0) {
          d.setDate(d.getDate() + 1);
          var day = d.getDay();
          if (day !== 0 && day !== 6 && holidays.indexOf(iso(d)) === -1) n--;
        }
        return d;
      }
      var locale = this.dataset.locale || 'es-MX';
      var fmt;
      try {
        fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' });
      } catch (e) {
        fmt = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'long' });
      }
      var from = this.querySelector('[data-ns-from]');
      var to = this.querySelector('[data-ns-to]');
      if (!from || !to) return;
      from.textContent = fmt.format(addBusiness(start, min));
      to.textContent = fmt.format(addBusiness(start, max));
      this.querySelector('[data-ns-eta-dates]').hidden = false;
      this.querySelector('[data-ns-eta-days]').hidden = true;
    }
  }
  define('ns-eta', NsEta);

  /* ------------------------------------------------------------------ */
  /* Promo tile inside the native collection grid                        */
  /* ------------------------------------------------------------------ */
  class NsGridPromo extends HTMLElement {
    connectedCallback() {
      this.after = parseInt(this.dataset.after, 10) || 4;
      this.span = parseInt(this.dataset.span, 10) || 2;
      this.template = this.querySelector('template');
      if (!this.template) return;
      this.place();
      var host = document.querySelector('[ref="grid"]');
      if (host && 'MutationObserver' in window) {
        this.mo = new MutationObserver(() => {
          clearTimeout(this.t);
          this.t = setTimeout(() => this.place(), 120);
        });
        this.mo.observe(host.parentElement || host, { childList: true, subtree: true });
      }
    }

    disconnectedCallback() {
      if (this.mo) this.mo.disconnect();
      if (this.tile && this.tile.parentElement) this.tile.remove();
    }

    /* Page 2+, native filters (also applied by AJAX) or tag URLs */
    isFiltered() {
      if (this.hasAttribute('data-filtered')) return true;
      var params = new URLSearchParams(window.location.search);
      if (parseInt(params.get('page') || '1', 10) > 1) return true;
      var filtered = false;
      params.forEach(function (_v, key) {
        if (key.indexOf('filter.') === 0 || key === 'q') filtered = true;
      });
      return filtered;
    }

    place() {
      var grid = document.querySelector('[ref="grid"]');
      if (!grid) return;
      var current = grid.querySelector(':scope > .ns-grid-tile');
      if (this.isFiltered()) {
        if (current) current.remove();
        return;
      }
      if (current) return;
      var cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
      if (cards.length <= this.after) return;
      var tile = this.template.content.firstElementChild.cloneNode(true);
      tile.style.gridColumn = 'span ' + this.span;
      cards[this.after - 1].after(tile);
      this.tile = tile;
    }
  }
  define('ns-grid-promo', NsGridPromo);

  /* ------------------------------------------------------------------ */
  /* Product card button (full width, under the card)                    */
  /* It does not add anything by itself: it triggers the card's NATIVE   */
  /* quick add. Products with options open Horizon's quick-add popup;    */
  /* single-variant products use the native add-to-cart form.            */
  /* ------------------------------------------------------------------ */
  function cardParts(btn) {
    var card = btn.closest('product-card, .product-card');
    var qa = card ? card.querySelector('quick-add-component') : null;
    return { card: card, qa: qa };
  }

  /* Cards with the in-card size picker (northside-card-sizes.js) are not proxied */
  function inCardPicker(btn) {
    return !!btn.closest('ns-card-atc[data-mode="sizes"], ns-card-atc[data-mode="single"]');
  }

  function syncCardButtons() {
    document.querySelectorAll('[data-ns-card-atc]:not([data-ns-ready])').forEach(function (btn) {
      var parts = cardParts(btn);
      var label = btn.querySelector('[data-ns-card-atc-label]') || btn;
      btn.setAttribute('data-ns-ready', '');
      if (!parts.card) return;
      parts.card.classList.add('ns-has-card-atc');
      if (inCardPicker(btn)) return;
      if (!parts.qa) {
        label.textContent = btn.dataset.labelView || label.textContent;
        return;
      }
      if (parts.qa.dataset.quickAddButton === 'add') {
        var add = parts.qa.querySelector('add-to-cart-component button[type="submit"]');
        if (add && add.disabled) {
          btn.disabled = true;
          label.textContent = btn.dataset.labelSoldout || label.textContent;
        } else {
          label.textContent = btn.dataset.labelAdd || label.textContent;
        }
      }
    });
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-ns-card-atc]');
    if (!btn || btn.disabled || inCardPicker(btn)) return;
    event.preventDefault();
    var parts = cardParts(btn);
    if (!parts.card) return;
    var native = null;
    if (parts.qa) {
      native = parts.qa.dataset.quickAddButton === 'add'
        ? parts.qa.querySelector('add-to-cart-component button[type="submit"]')
        : parts.qa.querySelector('.quick-add__button--choose');
    }
    if (native) {
      native.click();
    } else {
      var link = parts.card.querySelector('a[href*="/products/"]');
      if (link) window.location.href = link.href;
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncCardButtons);
  else syncCardButtons();
  if ('MutationObserver' in window) {
    var cardTimer = null;
    new MutationObserver(function () {
      clearTimeout(cardTimer);
      cardTimer = setTimeout(syncCardButtons, 150);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ */
  /* CTA that reuses the NATIVE add to cart button of the product form   */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var cta = event.target.closest('[data-ns-native-atc]');
    if (!cta) return;
    event.preventDefault();
    var btn =
      document.querySelector('.product-information add-to-cart-component button[type="submit"]') ||
      document.querySelector('product-form-component add-to-cart-component button[type="submit"]') ||
      document.querySelector('form[action*="/cart/add"] button[type="submit"][name="add"]');
    if (!btn) return;
    btn.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    if (!btn.disabled) {
      setTimeout(function () {
        btn.click();
      }, reduceMotion ? 0 : 350);
    }
  });
})();
